import {
  Button,
  ColorPicker,
  message,
  Space,
  Spin,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';

const { Dragger } = Upload;

// Mirrors the GET/PATCH/POST response shape from routes/branding.py.
interface BrandingData {
  logo_url: string | null;
  primary_color_hex: string | null;
  font_url: string | null;
  style_guide_doc_url: string | null;
  is_complete: boolean;
}

interface BrandingConfigProps {
  accessToken: string;
  apiUrl: string;
  clientId: number;
  setError: (error: string | undefined) => void;
}

// Matches server-side limits in routes/branding.py so we fail fast with a clear
// message instead of round-tripping to a 413.
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const MAX_FONT_BYTES = 5 * 1024 * 1024;
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export function BrandingConfig({
  accessToken,
  apiUrl,
  clientId,
  setError,
}: BrandingConfigProps) {
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [colorHex, setColorHex] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [savingColor, setSavingColor] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(false);
  const [colorTouched, setColorTouched] = useState(false);

  const brandingUrl = `${apiUrl}/api/clients/${clientId}/branding`;

  const applyBranding = (data: BrandingData) => {
    setBranding(data);
    setColorHex(data.primary_color_hex ?? '');
  };

  useEffect(() => {
    const fetchBranding = async () => {
      setLoading(true);
      try {
        const response = await fetch(brandingUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        applyBranding(await response.json());
      } catch (error) {
        setError(`Failed to load branding: ${error}`);
      } finally {
        setLoading(false);
      }
    };
    void fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingUrl, accessToken]);

  const handleSaveColor = async () => {
    setSavingColor(true);
    setError(undefined);
    try {
      const response = await fetch(brandingUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ primary_color_hex: colorHex }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      applyBranding(await response.json());
      setColorTouched(false);
      void message.success('Primary color saved');
    } catch (error) {
      setError(`Failed to save color: ${error}`);
    } finally {
      setSavingColor(false);
    }
  };

  const uploadAsset = async (kind: 'logo' | 'font', file: File) => {
    const setUploading = kind === 'logo' ? setUploadingLogo : setUploadingFont;
    setUploading(true);
    setError(undefined);
    try {
      const body = new FormData();
      body.append('file', file);
      // No Content-Type header — the browser sets the multipart boundary.
      const response = await fetch(`${brandingUrl}/${kind}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body,
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      applyBranding(await response.json());
      void message.success(`${kind === 'logo' ? 'Logo' : 'Font'} uploaded`);
    } catch (error) {
      setError(`Failed to upload ${kind}: ${error}`);
    } finally {
      setUploading(false);
    }
  };

  const deleteAsset = async (kind: 'logo' | 'font') => {
    setError(undefined);
    try {
      const response = await fetch(`${brandingUrl}/${kind}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      applyBranding(await response.json());
      void message.success(`${kind === 'logo' ? 'Logo' : 'Font'} removed`);
    } catch (error) {
      setError(`Failed to remove ${kind}: ${error}`);
    }
  };

  // beforeUpload does the client-side size check and hands off to uploadAsset,
  // returning false so antd never performs its own request.
  const makeBeforeUpload = (kind: 'logo' | 'font') => (file: File) => {
    const maxBytes = kind === 'logo' ? MAX_LOGO_BYTES : MAX_FONT_BYTES;
    if (file.size > maxBytes) {
      void message.error(
        `${kind === 'logo' ? 'Logo' : 'Font'} exceeds the ${maxBytes / (1024 * 1024)}MB limit`,
      );
      return Upload.LIST_IGNORE;
    }
    void uploadAsset(kind, file);
    return false;
  };

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <div
      style={{ textAlign: 'left', backgroundColor: 'white', padding: '20px' }}
    >
      <Typography.Title level={2}>Branding</Typography.Title>
      <Typography.Paragraph>
        Applied to this client&apos;s market reports (logo header, heading/table
        colors, contact block). A logo <b>and</b> a primary color are both
        required — with only one set, reports render unbranded.{' '}
        {branding?.is_complete ? (
          <Tag color="green">Complete</Tag>
        ) : (
          <Tag color="orange">Incomplete</Tag>
        )}
      </Typography.Paragraph>

      <Typography.Title level={4}>Logo</Typography.Title>
      {branding?.logo_url && (
        <div style={{ marginBottom: 12 }}>
          <img
            alt="Current logo"
            src={branding.logo_url}
            style={{
              maxHeight: 64,
              maxWidth: 320,
              border: '1px solid #f0f0f0',
              padding: 8,
              borderRadius: 4,
            }}
          />
          <div style={{ marginTop: 8 }}>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => void deleteAsset('logo')}
              size="small"
            >
              Remove logo
            </Button>
          </div>
        </div>
      )}
      <Dragger
        accept="image/png,image/jpeg,image/svg+xml"
        beforeUpload={makeBeforeUpload('logo')}
        disabled={uploadingLogo}
        maxCount={1}
        showUploadList={false}
      >
        <p className="ant-upload-drag-icon">
          {uploadingLogo ? <Spin /> : <InboxOutlined />}
        </p>
        <p className="ant-upload-text">Click or drag a logo to upload</p>
        <p className="ant-upload-hint">PNG, JPG, or SVG. Max 2MB, 2000px.</p>
      </Dragger>

      <Typography.Title level={4} style={{ marginTop: 24 }}>
        Primary Color
      </Typography.Title>
      <Space>
        <ColorPicker
          disabled={savingColor}
          disabledAlpha
          format="hex"
          onChange={color => {
            setColorHex(color.toHexString());
            setColorTouched(true);
          }}
          showText
          value={colorHex || undefined}
        />
        <Button
          disabled={!colorTouched || !HEX_COLOR_RE.test(colorHex)}
          loading={savingColor}
          onClick={() => void handleSaveColor()}
          type="primary"
        >
          Save color
        </Button>
      </Space>

      <Typography.Title level={4} style={{ marginTop: 24 }}>
        Font <Typography.Text type="secondary">(optional)</Typography.Text>
      </Typography.Title>
      {branding?.font_url && (
        <div style={{ marginBottom: 12 }}>
          <Tag color="blue">Custom font uploaded</Tag>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => void deleteAsset('font')}
            size="small"
            style={{ marginLeft: 8 }}
          >
            Remove font
          </Button>
        </div>
      )}
      <Dragger
        accept=".ttf,.otf"
        beforeUpload={makeBeforeUpload('font')}
        disabled={uploadingFont}
        maxCount={1}
        showUploadList={false}
      >
        <p className="ant-upload-drag-icon">
          {uploadingFont ? <Spin /> : <InboxOutlined />}
        </p>
        <p className="ant-upload-text">Click or drag a font to upload</p>
        <p className="ant-upload-hint">
          TTF or OTF, max 5MB. Applied to body and headings. Confirm the license
          permits embedding.
        </p>
      </Dragger>
    </div>
  );
}
